import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import {
  ChevronLeft,
  ChevronRight,
  FileText,
  Search,
} from "lucide-react";
import TaxTable, { TaxTdsRecord } from "./TaxTable";
import DateRangePicker from "../../../../ecommerce/globalcomponents/DateRangePicker";
import { useAuth } from "../../../../context/AuthContext";
import "./TaxTdsPage.css";

type TdsReportApiRow = {
  quarter: string;
  quarterCode: string;
  year: number;
  referralIncentives: number;
  tdsAmount: number;
  payoutCount: number;
  tdsCertificate?: string;
};

type TdsReportResponse = {
  customerId: string;
  customerName: string;
  totalQuarters: number;
  totalReferralIncentives: number;
  totalTdsAmount: number;
  data: TdsReportApiRow[];
  paging: {
    total: number;
    page: number;
    limit: number;
    hasNext: boolean;
    hasPrevious: boolean;
  };
};

const formatCurrency = (value: number) =>
  new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value);

const Tax = () => {
  const { user } = useAuth();

  const [report, setReport] = useState<TdsReportResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [entries, setEntries] = useState(5);
  const [currentPage, setCurrentPage] = useState(1);
  const [dateRange, setDateRange] = useState({ from: "", to: "" });

  // Debounce search
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(search);
    }, 500);
    return () => clearTimeout(timer);
  }, [search]);

  useEffect(() => {
    setCurrentPage(1);
  }, [debouncedSearch, entries, dateRange]);

  useEffect(() => {
    if (!user?._id) return;
    if (dateRange.from && !dateRange.to) return;

    const fetchReport = async () => {
      setLoading(true);
      setError(null);
      try {
        const response = await axios.get(
          `${process.env.REACT_APP_API_BASE_URL}${process.env.REACT_APP_PAYOUT_PERFIX}/customer-reports/customer/${user._id}/tds-report`,
          {
            params: {
              page: currentPage,
              limit: entries,
              search: debouncedSearch || undefined,
              dateFrom: dateRange.from || undefined,
              dateTo: dateRange.to || undefined,
            },
            withCredentials: true,
          },
        );
        setReport(response.data);
      } catch (err: any) {
        setError(
          err.response?.data?.message ||
            "Failed to fetch TDS report",
        );
        setReport(null);
      } finally {
        setLoading(false);
      }
    };

    fetchReport();
  }, [
    user?._id,
    currentPage,
    entries,
    debouncedSearch,
    dateRange.from,
    dateRange.to,
  ]);

  const records: TaxTdsRecord[] = useMemo(
    () =>
      (report?.data || []).map((row, index) => ({
        id: `${row.year}-${row.quarterCode}-${index}`,
        quarter: row.quarter,
        quarterCode: row.quarterCode,
        year: row.year,
        totalReferralIncentives: row.referralIncentives,
        tdsPercentage:
          row.referralIncentives > 0
            ? Math.round((row.tdsAmount / row.referralIncentives) * 100)
            : 0,
        tdsAmount: row.tdsAmount,
        certificateName:
          row.tdsCertificate ||
          `TDS_CERT_${row.quarterCode}_${row.year}.pdf`,
      })),
    [report],
  );

  const totalReferral = report?.totalReferralIncentives ?? 0;
  const totalTds = report?.totalTdsAmount ?? 0;
  const averageTds = records.length
    ? records.reduce((sum, record) => sum + record.tdsPercentage, 0) /
      records.length
    : 0;

  const totalRecords = report?.paging?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(totalRecords / entries));

  const resultStart =
    totalRecords === 0 ? 0 : (currentPage - 1) * entries + 1;
  const resultEnd = Math.min(currentPage * entries, totalRecords);

  const handleCertificateDownload = (record: TaxTdsRecord) => {
    const content = [
      "Tax - TDS Certificate",
      "",
      `Certificate: ${record.certificateName}`,
      `Quarter: ${record.quarter} (${record.quarterCode} ${record.year})`,
      `Total Referral Incentives: ${formatCurrency(
        record.totalReferralIncentives,
      )}`,
      `TDS Percentage: ${record.tdsPercentage}%`,
      `TDS Amount: ${formatCurrency(record.tdsAmount)}`,
    ].join("\n");

    const blob = new Blob([content], {
      type: "text/plain;charset=utf-8",
    });
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement("a");
    anchor.href = url;
    anchor.download = record.certificateName.replace(".pdf", ".txt");
    document.body.appendChild(anchor);
    anchor.click();
    anchor.remove();
    URL.revokeObjectURL(url);
  };

  return (
    <section className="tax-tds-page">
      <div className="tax-tds-shell">
        <header className="tax-tds-header">
          <div className="tax-tds-heading">
            <div className="tax-tds-heading-icon">
              <FileText size={23} strokeWidth={2.2} />
            </div>

            <div className="tax-tds-heading-copy">
              <span className="tax-tds-eyebrow">Withdrawal</span>
              <h1>Tax - TDS</h1>
              <p>
                Review referral earnings, tax deductions and
                downloadable TDS certificates.
              </p>
            </div>
          </div>

          <div className="tax-tds-summary">
            <div className="tax-tds-summary-card">
              <span>Total Referral</span>
              <strong>{formatCurrency(totalReferral)}</strong>
            </div>
            <div className="tax-tds-summary-card">
              <span>Total TDS</span>
              <strong>{formatCurrency(totalTds)}</strong>
            </div>
            <div className="tax-tds-summary-card tax-tds-summary-card-primary">
              <span>Avg. TDS</span>
              <strong>{averageTds.toFixed(0)}%</strong>
            </div>
          </div>
        </header>

        <div className="tax-tds-toolbar">
          <div className="tax-tds-toolbar-left">
            <select
              value={entries}
              aria-label="Entries per page"
              onChange={(event) =>
                setEntries(Number(event.target.value))
              }
            >
              {[5, 10, 20].map((option) => (
                <option key={option} value={option}>
                  {option} Entries
                </option>
              ))}
            </select>

            <DateRangePicker
              value={dateRange}
              onChange={setDateRange}
            />

            <span className="tax-tds-record-count">
              {totalRecords} records
            </span>
          </div>

          <label className="tax-tds-search">
            <Search size={17} strokeWidth={2.2} />
            <input
              type="search"
              value={search}
              placeholder="Search Tax - TDS..."
              onChange={(event) => setSearch(event.target.value)}
            />
          </label>
        </div>

        <TaxTable
          records={records}
          resultStart={resultStart}
          loading={loading}
          error={error}
          onDownload={handleCertificateDownload}
        />

        <footer className="tax-tds-footer">
          <div className="tax-tds-results">
            Showing <strong>{resultStart}</strong> -{" "}
            <strong>{resultEnd}</strong> of{" "}
            <strong>{totalRecords}</strong>
          </div>

          <div className="tax-tds-pagination">
            <button
              type="button"
              disabled={currentPage === 1}
              aria-label="Previous page"
              onClick={() =>
                setCurrentPage((previous) =>
                  Math.max(1, previous - 1),
                )
              }
            >
              <ChevronLeft size={18} />
            </button>

            {Array.from(
              { length: totalPages },
              (_, index) => index + 1,
            ).map((page) => (
              <button
                type="button"
                key={page}
                className={page === currentPage ? "active" : ""}
                onClick={() => setCurrentPage(page)}
              >
                {page}
              </button>
            ))}

            <button
              type="button"
              disabled={currentPage === totalPages}
              aria-label="Next page"
              onClick={() =>
                setCurrentPage((previous) =>
                  Math.min(totalPages, previous + 1),
                )
              }
            >
              <ChevronRight size={18} />
            </button>
          </div>
        </footer>
      </div>
    </section>
  );
};

export default Tax;
